import type { ProfileExclusion, ProfileExclusionInput, ProfileSkill } from './profile.api';

export const PROFILE_EXCLUSION_TEXT_MAX_LENGTH = 60;

function normalizeText(value: string) {
  return value.replace(/\s+/g, ' ').trim();
}

function toIdentity(value: string) {
  return normalizeText(value).toLocaleLowerCase();
}

export function getExclusionKey(exclusion: ProfileExclusion) {
  return exclusion.skill
    ? `skill:${exclusion.skill.uuid}`
    : `text:${toIdentity(exclusion.title)}`;
}

function toExclusion(
  value: ProfileExclusion | ProfileSkill | string,
  skillsPool: ProfileSkill[]
): ProfileExclusion | null {
  if (typeof value !== 'string') {
    return 'skill' in value ? value : { skill: value, title: value.title };
  }

  const title = normalizeText(value).slice(0, PROFILE_EXCLUSION_TEXT_MAX_LENGTH);
  if (!title) {
    return null;
  }

  // Typed text that names a known Skill becomes that Skill.
  const matchingSkill = skillsPool.find((skill) => toIdentity(skill.title) === toIdentity(title));
  return matchingSkill
    ? { skill: matchingSkill, title: matchingSkill.title }
    : { skill: null, title };
}

// Turns what the autocomplete holds (saved items, picked Skills, typed text) into Exclusions,
// keeping the order and dropping repeats.
export function normalizeSelectedExclusions(
  selected: Array<ProfileExclusion | ProfileSkill | string>,
  skillsPool: ProfileSkill[] = []
): ProfileExclusion[] {
  const exclusions = new Map<string, ProfileExclusion>();
  const titles = new Set<string>();

  for (const value of selected) {
    const exclusion = toExclusion(value, skillsPool);
    if (!exclusion) {
      continue;
    }

    const key = getExclusionKey(exclusion);
    const titleIdentity = toIdentity(exclusion.title);
    if (exclusions.has(key) || (!exclusion.skill && titles.has(titleIdentity))) {
      continue;
    }

    exclusions.set(key, exclusion);
    titles.add(titleIdentity);
  }

  return Array.from(exclusions.values());
}

// A Skill is never both among a Profile's skills and its Exclusions, so each list's options
// leave out the Skills taken by the other.
export function withoutSkills(skills: ProfileSkill[], takenSkillUuids: string[]) {
  if (!takenSkillUuids.length) {
    return skills;
  }

  const taken = new Set(takenSkillUuids);
  return skills.filter((skill) => !taken.has(skill.uuid));
}

export function getExcludedSkillUuids(exclusions: ProfileExclusion[]) {
  return exclusions.flatMap((exclusion) => (exclusion.skill ? [exclusion.skill.uuid] : []));
}

export function toExclusionInputs(
  exclusions?: ProfileExclusion[]
): ProfileExclusionInput[] | undefined {
  if (!exclusions) {
    return undefined;
  }

  return exclusions.map((exclusion) =>
    exclusion.skill
      ? { skillUuid: exclusion.skill.uuid }
      : { title: normalizeText(exclusion.title) }
  );
}
